import { motion } from 'motion/react';
import { SignUpButton, TalkToFoundersButton } from './CTAButtons';

const ease = [0.16, 1, 0.3, 1] as const;

const tierVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.13 } },
};
const tierItem = {
  hidden: { opacity: 0, y: 52, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 1, ease } },
};

function TierCard({ children, featured = false }: { children: React.ReactNode; featured?: boolean }) {
  return (
    /* The outer element is the hover target and never moves. If the card itself owned
       :hover, lifting it 10px would slide its own bottom edge out from under the cursor,
       ending the hover, dropping it back, and oscillating for as long as the pointer
       rested near an edge. */
    <motion.div className="group h-full" variants={tierItem}>
      {/* tier class retained for .tier.featured::before gradient-border pseudo-element */}
      <div
        className={`tier bg-surface-container rounded-[6px] p-8 flex flex-col h-full min-h-[520px] relative transition-[transform,background,box-shadow] duration-[600ms] [transition-timing-function:cubic-bezier(0.16,1,0.3,1)] group-hover:bg-surface-container-high group-hover:-translate-y-[10px] group-hover:[box-shadow:0_40px_80px_-24px_rgba(0,0,0,0.7)] ${featured ? 'featured' : ''}`}
      >
        {children}
      </div>
    </motion.div>
  );
}

/**
 * Self-serve tiers lead with signup — the plan is buyable without us — and keep talking
 * to sales beside it. The buttons grow from their natural width rather than being
 * squeezed: `.btn` never wraps its label, so on a card too narrow for both they wrap to
 * their own rows instead of overflowing.
 */
function TierActions({ children }: { children: React.ReactNode }) {
  return <div className="mt-6 flex flex-wrap gap-2.5">{children}</div>;
}

const TIER_BTN = 'grow shrink-0 basis-auto justify-center';

type PricingProps = {
  titleAs?: 'h1' | 'h2';
};

export function Pricing({ titleAs = 'h1' }: PricingProps) {
  const Heading = titleAs;

  return (
    <section className="section band-light low" id="pricing">
      <div className="container">
        <motion.div
          className="flex justify-between items-end gap-10 mb-14 max-md:flex-col max-md:items-start"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 1, ease }}
        >
          <Heading className="home-section-title m-0 max-w-[16ch]">
            Pricing that scales<br />with the production.
          </Heading>
          <p className="body-lg max-w-[42ch]">
            Flexible plans designed to support teams of any size, at any stage of growth.          </p>
        </motion.div>

        <motion.div
          className="grid grid-cols-3 gap-5 max-lg:grid-cols-2 max-sm:grid-cols-1"
          variants={tierVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          <TierCard>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-primary mb-[14px] flex gap-[10px] items-center">
              <span>TIER 01</span><span>· BASIC</span>
            </div>
            <h3 className="font-display text-[36px] font-medium m-0 mb-[6px] tracking-[-0.015em]">Basic</h3>
            <p className="text-on-surface-variant text-[15px] leading-[1.5] m-0 mb-6 max-w-[32ch]">
              For solo filmmakers and creators shipping their first AI-native shots.
            </p>
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-on-surface-muted mb-2">1,000 credits / month</div>
            <div className="font-display text-[40px] font-light text-on-surface tracking-[-0.02em] mb-[6px]">
              ₹4,499.00 <sub className="font-mono text-[11px] text-on-surface-muted tracking-[0.14em] uppercase align-middle ml-2">+ GST (18%)</sub>
            </div>
            <ul className="list-none p-0 mt-6 flex flex-col gap-[14px] flex-1">
              {['Access to all top models', 'Single creator seat', 'Email support'].map(f => (
                <li key={f} className="text-[14px] text-on-surface-variant flex gap-3 leading-[1.5]">{f}</li>
              ))}
            </ul>
            <TierActions>
              <SignUpButton variant="ghost" analyticsLocation="pricing" className={TIER_BTN} />
              <TalkToFoundersButton variant="ghost" analyticsLocation="pricing" className={TIER_BTN} />
            </TierActions>
          </TierCard>

          <TierCard featured>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-primary mb-[14px] flex gap-[10px] items-center">
              <span>TIER 02</span><span>· STUDIO</span>
            </div>
            <h3 className="font-display text-[36px] font-medium m-0 mb-[6px] tracking-[-0.015em]">Studio</h3>
            <p className="text-on-surface-variant text-[15px] leading-[1.5] m-0 mb-6 max-w-[32ch]">
              For production teams and agencies building coherent worlds at scale.
            </p>
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-on-surface-muted mb-2">3,000 credits / month</div>
            <div className="font-display text-[40px] font-light text-on-surface tracking-[-0.02em] mb-[6px]">
              ₹12,499.00 <sub className="font-mono text-[11px] text-on-surface-muted tracking-[0.14em] uppercase align-middle ml-2">+ GST (18%)</sub>
            </div>
            <ul className="list-none p-0 mt-6 flex flex-col gap-[14px] flex-1">
              {['Rhapsody engine', 'Team workspace (up to 5 seats)', 'Priority support'].map(f => (
                <li key={f} className="text-[14px] text-on-surface-variant flex gap-3 leading-[1.5]">{f}</li>
              ))}
            </ul>
            <TierActions>
              <SignUpButton analyticsLocation="pricing" className={TIER_BTN} />
              <TalkToFoundersButton variant="ghost" analyticsLocation="pricing" className={TIER_BTN} />
            </TierActions>
          </TierCard>

          <TierCard>
            <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-primary mb-[14px] flex gap-[10px] items-center">
              <span>TIER 03</span><span>· ENTERPRISE</span>
            </div>
            <h3 className="font-display text-[36px] font-medium m-0 mb-[6px] tracking-[-0.015em]">Enterprise</h3>
            <p className="text-on-surface-variant text-[15px] leading-[1.5] m-0 mb-6 max-w-[32ch]">
              Custom infrastructure, API, and dedicated support for enterprises.
            </p>
            <div className="font-display text-[40px] font-light text-on-surface tracking-[-0.02em] mb-[6px]">
              Custom<sub className="font-mono text-[11px] text-on-surface-muted tracking-[0.14em] uppercase align-middle ml-2">talk to us</sub>
            </div>
            <ul className="list-none p-0 mt-6 flex flex-col gap-[14px] flex-1">
              {['Unlimited rendered output, dedicated cluster', 'Private continuity models + VPC option', 'API, webhooks, pipeline integrations', 'Named engineer + 24/7 support'].map(f => (
                <li key={f} className="text-[14px] text-on-surface-variant flex gap-3 leading-[1.5]">{f}</li>
              ))}
            </ul>
            <TierActions>
              {/* Enterprise is not self-serve, so its one button owns the row. */}
              <TalkToFoundersButton variant="ghost" analyticsLocation="pricing" className={`${TIER_BTN} w-full`} />
            </TierActions>
          </TierCard>
        </motion.div>
      </div>
    </section>
  );
}
